import { useTranslator } from '@bluebeam/react-i18n';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import React, { useEffect, useState } from 'react';
import { RawStudioItem } from 'records/StudioItem';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import {
  StudioProjectFolder,
  StudioProjectFolderData,
} from 'records/StudioProjectFolder';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import StudioFolderListRow from './StudioFolderListRow';

const StudioProjectsList: React.FC<WithAppStoreProps> = ({
  actions,
  appStore,
}: WithAppStoreProps) => {
  const {
    studioProjects,
    studioCurrentFolder,
    studioCurrentFolderName,
    studioExportFolder,
    studioCurrentProjectId,
  } = appStore.state;

  const { translate } = useTranslator(['fileBrowser']);
  const gridTemplateColumns = '50px auto 150px';

  const [folders, setFolders] = useState<StudioProjectFolderData[]>([]);
  const [files, setFiles] = useState<StudioProjectFileData[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState('');

  const handleItemSelected = async (item: RawStudioItem): Promise<void> => {
    if (item.type === 'project') {
      const projectId = item.value.id.toString();
      if (studioCurrentProjectId !== projectId) {
        await actions.setStudioProjectId(projectId);
        await actions.setStudioExportFolder(new StudioProjectFolder());
      } else {
        await actions.setStudioProjectId('');
        await actions.setStudioExportFolder(null);
      }
      // }
    } else if (item.type === 'folder') {
      const folder = new StudioProjectFolder(item.value);
      if (studioExportFolder?.id !== folder.id) {
        await actions.setStudioExportFolder(folder);
      } else {
        await actions.setStudioExportFolder(null);
      }
    }
  };

  const handleOpenItem = async (item: RawStudioItem) => {
    let projectId = '';
    let folderId: string | number = '';

    switch (item.type) {
      case 'folder':
        projectId = currentProjectId;
        folderId = item.value.id;
        break;
      default:
        projectId = item.value.id.toString();
        await actions.setStudioProjectId(projectId);
        setCurrentProjectId(projectId);
        break;
    }
    await actions.setStudioFolderName(item.value.name);
    await actions.getStudioProjectFolder(projectId, folderId);
    await actions.setFolderSelectCurrentId(folderId.toString());
  };

  const createListRow = (item: RawStudioItem) => {
    const isFile = item.value.toString().includes('StudioProjectFile');
    return (
      <StudioFolderListRow
        onClick={() => (!isFile ? handleOpenItem(item) : null)}
        onChecked={() => handleItemSelected(item)}
        checked={
          studioExportFolder?.id === item.value.id ||
          (studioCurrentProjectId === item.value.id &&
            studioExportFolder?.id === 0)
        }
        key={item.value.id}
        item={item}
      />
    );
  };

  useEffect(() => {
    if (studioCurrentFolder) {
      setFolders(studioCurrentFolder.folders.toArray());
      setFiles(studioCurrentFolder.files.toArray());
    }
  }, [studioCurrentFolder, studioExportFolder]);

  return (
    <StdList id="studio-project-list" gridTemplateColumns={gridTemplateColumns}>
      <StdListHeader>
        <StdListHeaderCheckbox
          id="studio-project-export-select-all-checkbox"
          indeterminate={false}
          checked={false}
        />
        <StdListHeaderCell text={translate('fileBrowser:name')} />
        <StdListHeaderCell text={translate('fileBrowser:lastModifiedDate')} />
      </StdListHeader>
      <StdListBody>
        {studioCurrentFolderName === '' &&
          studioProjects.map((p) =>
            createListRow({ value: p, type: 'project' })
          )}
        {studioCurrentFolder.folders &&
          folders.map((folder) =>
            createListRow({ value: folder, type: 'folder' })
          )}
        {studioCurrentFolder.files &&
          files.map((document) =>
            createListRow({ value: document, type: 'file' })
          )}
      </StdListBody>
    </StdList>
  );
};

export default withAppStore(StudioProjectsList);
